/**
 * Formats a decimal string for display without going through a float.
 *
 * @param {string} value - Decimal text from the API.
 * @param {number} places - Places to show.
 * @returns {string} The value trimmed or padded to the requested places.
 *
 * @remarks
 * Truncates rather than rounds. These figures are already rounded by the
 * service that produced them, and rounding a second time here would make the
 * screen disagree with the ledger in the last place.
 */
export const formatDecimal = (value, places = 2) => {
  const text = String(value ?? "0").trim();

  if (!/^-?\d+(\.\d+)?$/.test(text)) {
    return "—";
  }

  const negative = text.startsWith("-");
  const [whole, fraction = ""] = (negative ? text.slice(1) : text).split(".");

  if (places === 0) {
    return `${negative ? "-" : ""}${whole}`;
  }

  const padded = fraction.padEnd(places, "0").slice(0, places);

  return `${negative ? "-" : ""}${whole}.${padded}`;
};

/**
 * Describes what a markup rate means in money.
 *
 * @param {string} markupPercent - Markup, e.g. "25.000".
 * @returns {string} A worked example for a one-dollar call.
 *
 * @remarks
 * A percentage alone is easy to misread as a margin on the sale price rather
 * than an addition to cost. Showing the arithmetic on a round number removes
 * the ambiguity without a paragraph of explanation.
 */
export const markupExample = (markupPercent) => {
  const percent = Number(markupPercent);

  if (!Number.isFinite(percent)) {
    return "";
  }

  return `A call costing $1.00 bills the customer $${(1 + percent / 100).toFixed(2)}.`;
};

/**
 * Describes the display ratio in both directions.
 *
 * @param {string} creditsPerUsd - Credits per dollar, e.g. "10.000000".
 * @returns {string} What a dollar buys and what a credit is worth.
 */
export const ratioExample = (creditsPerUsd) => {
  const ratio = Number(creditsPerUsd);

  if (!Number.isFinite(ratio) || ratio <= 0) {
    return "";
  }

  return `$1 buys ${formatDecimal(String(ratio), 2)} credits, so 1 credit is worth $${(
    1 / ratio
  ).toFixed(4)}.`;
};

/**
 * Names a company for display.
 *
 * @param {object} company - A row from the company list.
 * @returns {string} The registered name, or the account id when it has none.
 *
 * @remarks
 * Falls back to the id rather than showing an empty option. An unnamed account
 * is still an account somebody may need to grant a discount to, and a blank row
 * in the list is indistinguishable from a loading failure.
 */
export const companyLabel = (company) =>
  company?.register_company_name || `Company ${company?.id}`;

/**
 * Whether a discount is in force today.
 *
 * @param {{validFrom: string, validTo: string|null}} discount - Discount row.
 * @param {string} today - Today as YYYY-MM-DD.
 * @returns {boolean} True when today falls inside the window.
 *
 * @remarks
 * Compares ISO date strings directly. They sort lexicographically in calendar
 * order, so this avoids constructing Date objects that would drag the browser's
 * timezone into a decision the server makes in UTC.
 */
export const isDiscountActive = (discount, today) => {
  if (!discount?.validFrom) {
    return false;
  }

  const from = String(discount.validFrom).slice(0, 10);
  const to = discount.validTo ? String(discount.validTo).slice(0, 10) : null;

  return from <= today && (!to || to >= today);
};

/**
 * Today's date in the form the API expects.
 *
 * @returns {string} YYYY-MM-DD in UTC.
 */
export const todayIso = () => new Date().toISOString().slice(0, 10);
